/*
  The Ranch Gallery — cascading option resolution for the variant step selector.

  Why this exists: unavailable/implicit option values are dropped from the DOM
  entirely (see product-variant-options.liquid), so a step can be hidden (e.g.
  Format for oil paintings, Size for Digital File Only). Shopify's
  section-render endpoint does NOT intelligently pick "the first available
  value given what else is selected" for an option value it wasn't sent — it
  falls back to the product's global first variant, ignoring everything else
  that WAS sent. That breaks the picker (wrong step shown, wrong selection)
  unless every request carries an explicit, valid value for all 3 options.

  Fix: on every option change, rebuild the full 3-option selection:
  - Seed it from the last resolved variant (`data-selected-variant`), which
    always has a value for every position — including hidden ones — because
    it reflects what the server actually picked, not just what's checked in
    the DOM.
  - Overlay the option that just changed.
  - Cascade forward through the later positions using the embedded variant
    matrix, to land on a real, available variant.
  - Make sure every position has a matching checked input before Dawn's own
    <variant-selects> change handler (global.js, bubble phase) reads the
    selection: reuse a real rendered radio if one exists, otherwise inject a
    hidden one. This listener runs in the capture phase so it always runs
    first.
*/
document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const changedPosition = parseInt(target.dataset.optionPosition || '', 10);
    if (!changedPosition) return;

    const variantSelects = target.closest('variant-selects');
    if (!variantSelects) return;

    const matrixEl = variantSelects.querySelector('[data-variant-matrix]');
    const mapEl = variantSelects.querySelector('[data-option-value-map]');
    const selectedVariantEl = variantSelects.querySelector('[data-selected-variant]');
    if (!matrixEl || !mapEl) return;

    let variants;
    let valueMap;
    try {
      variants = JSON.parse(matrixEl.textContent);
      valueMap = JSON.parse(mapEl.textContent);
    } catch {
      return;
    }

    const positions = Object.keys(valueMap)
      .map((p) => parseInt(p, 10))
      .sort((a, b) => a - b);

    // Seed from the last resolved variant — the only source that reliably
    // has a value for every position, hidden ones included.
    const desired = {};
    if (selectedVariantEl) {
      try {
        const selectedVariant = JSON.parse(selectedVariantEl.textContent);
        positions.forEach((pos) => {
          const value = selectedVariant?.['option' + pos];
          if (value !== undefined && value !== null) desired[pos] = value;
        });
      } catch {
        // ignore, fall through with whatever the DOM has checked
      }
    }
    // Live checked radios (if any) reflect the truest current intent.
    variantSelects.querySelectorAll('input[type="radio"][data-option-position]:checked').forEach((input) => {
      desired[input.dataset.optionPosition] = input.value;
    });
    // Apply the value that just changed, and drop everything after it so it
    // gets recomputed against the new selection.
    desired[changedPosition] = target.value;
    positions.forEach((pos) => {
      if (pos > changedPosition) delete desired[pos];
    });

    // Cascade forward: for each later position, find the first available
    // variant consistent with everything decided so far and adopt its value.
    positions.forEach((pos) => {
      if (pos <= changedPosition) return;
      const match = variants.find(
        (v) =>
          v.available &&
          positions.every((p) => {
            if (p >= pos) return true;
            return desired[p] === undefined || v['o' + p] === desired[p];
          })
      );
      if (match) desired[pos] = match['o' + pos];
    });

    // Uncheck every later-position radio and drop previous shadow inputs —
    // they're about to be rebuilt from `desired`.
    variantSelects.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
      if (parseInt(input.dataset.optionPosition, 10) > changedPosition) input.checked = false;
    });
    variantSelects.querySelectorAll('[data-cascade-shadow]').forEach((el) => el.remove());

    const fieldset = variantSelects.querySelector('fieldset');
    if (!fieldset) return;

    // Every position other than the one just clicked needs an explicit
    // checked input reflecting `desired` — including positions BEFORE the
    // changed one whose step is hidden (e.g. Format while on the oil path),
    // since a full server re-render never leaves a real or shadow input
    // behind for those once the DOM is swapped.
    positions.forEach((pos) => {
      if (pos === changedPosition) return;
      const value = desired[pos];
      const id = value !== undefined ? valueMap[pos]?.[value] : undefined;
      if (id === undefined) return;

      const existing = variantSelects.querySelector(
        `input[type="radio"][data-option-position="${pos}"][data-option-value-id="${id}"]`
      );
      if (existing) {
        existing.checked = true;
        return;
      }

      // The value isn't currently rendered (its step is hidden/filtered) —
      // add an invisible checked input so Dawn's selectedOptionValues getter
      // (`fieldset input:checked`) still includes it in the request.
      const shadow = document.createElement('input');
      shadow.type = 'radio';
      shadow.hidden = true;
      // Without this, a bare radio defaults its value to "on". That's fine
      // for Dawn's own selectedOptionValues getter (keys off data-option-
      // value-id, not .value) -- but THIS file's own "live checked radios"
      // overlay a few lines up reads .value to reseed `desired` on the next
      // change before this shadow gets torn down and rebuilt. Two changes
      // landing back-to-back (no server round trip in between -- easy to
      // hit at a normal click cadence once real network latency is in play)
      // read that "on" as if it were the real option value, which doesn't
      // match anything in valueMap, silently dropping this position from
      // the next request entirely and letting Shopify fall back to the
      // product's global first variant -- exactly how a whole step (e.g.
      // Size) could disappear after a couple of quick clicks.
      shadow.value = value;
      shadow.dataset.optionPosition = String(pos);
      shadow.dataset.optionValueId = String(id);
      shadow.dataset.cascadeShadow = 'true';
      shadow.checked = true;
      fieldset.appendChild(shadow);
    });
  },
  true
);
